import LeaseRecordInput from 'src/components/leases/LeaseRecordInput';
import React, { useEffect, useState } from 'react';
import useFetch from 'use-http';
import { X, Loader2 } from 'src/components/common/icons';
import { Field, SelectField } from './Master';
import { ITableConfig, ITableColumn, Table } from 'src/components/common/table';
import PagedResultSet from 'src/models/common/PagedResultSet';
import Pager from 'src/components/common/table/Pager';
import { ILeaseRecordsApiError, LeaseRecordsErrorAlert, parseLeaseRecordsApiError } from '../LeaseRecordsApiError';

const PAGE_SIZE = 15;

interface IGLInquiry {
    entryNo: number;
    acctNo: string | null;
    postDate: string | null;
    dscr: string | null;
    dscr2: string | null;
    refNo: string | null;
    typ: string | null;
    amt: number | null;
    clearBank: string | null;
    statementDate: string | null;
    delFlag: number | null;
    id?: string;
}

interface IProps {
    isOpen: boolean;
    onClose: () => void;
}

const GLInquiryModal: React.FC<IProps> = ({ isOpen, onClose }) => {
    const { get, response, loading } = useFetch();

    // --- Data State ---
    const [glData, setGlData] = useState<PagedResultSet<IGLInquiry> | undefined>();
    const [pageNumber, setPageNumber] = useState(1);
    const [shouldReload, setShouldReload] = useState(true);

    // --- Search Filter States ---
    const [filterEntryNo, setFilterEntryNo] = useState('');
    const [filterAcctNo, setFilterAcctNo] = useState('');
    const [filterPostDate, setFilterPostDate] = useState('');
    const [filterPayee, setFilterPayee] = useState('');
    const [filterDescription, setFilterDescription] = useState('');
    const [filterRefNo, setFilterRefNo] = useState('');
    const [filterType, setFilterType] = useState('');
    const [filterAmount, setFilterAmount] = useState('');
    const [filterCl, setFilterCl] = useState(''); // "1" or "0"
    const [filterStatementDate, setFilterStatementDate] = useState('');

    // --- Report Modal States ---
    const [isReportModalOpen, setIsReportModalOpen] = useState(false);
    const [isGeneratingReport, setIsGeneratingReport] = useState(false);
    const [reportAcctNo, setReportAcctNo] = useState('');
    const [reportBegDate, setReportBegDate] = useState('');
    const [reportEndDate, setReportEndDate] = useState('');
    const [reportSortType, setReportSortType] = useState('1');
    const [requestError, setRequestError] = useState<ILeaseRecordsApiError | null>(null);

    useEffect(() => {
        const loadData = async (): Promise<void> => {
            const skip = (pageNumber - 1) * PAGE_SIZE;
            const params = new URLSearchParams();

            params.append('skip', skip.toString());
            params.append('top', PAGE_SIZE.toString());

            if (filterEntryNo.trim()) {
                params.append('entryNo', filterEntryNo.trim());
            }
            if (filterAcctNo.trim()) {
                params.append('acctNo', filterAcctNo.trim());
            }
            if (filterPostDate.trim()) {
                params.append('postDate', filterPostDate.trim());
            }
            if (filterPayee.trim()) {
                params.append('payee', filterPayee.trim());
            }
            if (filterDescription.trim()) {
                params.append('description', filterDescription.trim());
            }
            if (filterRefNo.trim()) {
                params.append('refNo', filterRefNo.trim());
            }
            if (filterType.trim()) {
                params.append('type', filterType.trim());
            }
            if (filterAmount.trim()) {
                params.append('amount', filterAmount.trim());
            }
            if (filterCl.trim()) {
                params.append('cl', filterCl.trim());
            }
            if (filterStatementDate.trim()) {
                params.append('statementDate', filterStatementDate.trim());
            }

            const data = await get(`/v1.0/leases/admin/gl-inquiry?${params.toString()}`);

            if (response.ok && data) {
                const pagedResult = new PagedResultSet<IGLInquiry>();
                pagedResult.items = data.items.map((i: IGLInquiry) => ({ ...i, id: i.entryNo.toString() }));
                pagedResult.totalRecords = data.totalCount;
                pagedResult.pageNumber = pageNumber;
                pagedResult.pageSize = PAGE_SIZE;

                setGlData(pagedResult);
            }
        };

        if (isOpen && shouldReload) {
            loadData();
            setShouldReload(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, shouldReload, pageNumber]);

    const handleSearch = (): void => {
        setPageNumber(1);
        setShouldReload(true);
    };

    const handleClearFilters = (): void => {
        setFilterEntryNo('');
        setFilterAcctNo('');
        setFilterPostDate('');
        setFilterPayee('');
        setFilterDescription('');
        setFilterRefNo('');
        setFilterType('');
        setFilterAmount('');
        setFilterCl('');
        setFilterStatementDate('');

        setPageNumber(1);
        setShouldReload(true);
    };

    const handlePageChange = (newPageNumber: number): void => {
        setPageNumber(newPageNumber);
        setShouldReload(true);
    };

    const handleKeyPress = (e: React.KeyboardEvent): void => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    // --- SSRS Report Blob Download Flow ---
    const handleGenerateReport = async (): Promise<void> => {
        if (!reportAcctNo || !reportBegDate || !reportEndDate || !reportSortType) {
            alert('All report parameters are required.');
            return;
        }

        setIsGeneratingReport(true);
        setRequestError(null);

        const formatForSSRS = (dateString: string): string => {
            const parts = dateString.split('-');
            if (parts.length === 3) {
                return `${parts[1]}/${parts[2]}/${parts[0]}`; // MM/DD/YYYY
            } else {
                return dateString;
            }
        };

        const begDateStr = formatForSSRS(reportBegDate);
        const endDateStr = formatForSSRS(reportEndDate);

        const params = new URLSearchParams();
        params.append('acctNo', reportAcctNo);
        params.append('begDate', begDateStr);
        params.append('endDate', endDateStr);
        params.append('sortType', reportSortType);
        const url = `/v1.0/leases/admin/gl-inquiry/report?${params.toString()}`;

        const safeAcct = reportAcctNo.replace(/%/g, 'ALL');
        const fileName = `LeaseGLDetail_${safeAcct}.xlsx`;

        try {
            // 1. Make the request using useFetch
            await get(url);

            // 2. Check Success
            if (response.ok) {
                // 3. Get Blob
                const blob = await response.blob();

                // 4. Create Download Link
                const downloadUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.download = fileName; // Force download with specific name
                document.body.appendChild(link);

                // 5. Trigger Click & Cleanup
                link.click();
                link.remove();
                window.URL.revokeObjectURL(downloadUrl);

                setIsReportModalOpen(false); // Close the modal upon success
            } else {
                setRequestError(parseLeaseRecordsApiError(response.data || { title: 'Failed to generate report.' }, response.status));
            }
        } catch (error) {
            // Download failed — error captured by catch block
            setRequestError(parseLeaseRecordsApiError({ title: 'Error downloading file.' }, 500));
        } finally {
            setIsGeneratingReport(false);
        }
    };

    // --- Formatters ---
    const formatDateForDisplay = (dateStr?: string | null): string => {
        if (!dateStr) {
            return '—';
        }
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) {
            return '—';
        }
        return d.toLocaleDateString('en-US');
    };

    const formatCurrency = (amt?: number | string | null): string => {
        if (amt === null || amt === undefined) {
            return '—';
        }
        const num = typeof amt === 'string' ? parseFloat(amt) : amt;
        if (isNaN(num)) {
            return '—';
        }
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
    };

    const formatCheckbox = (value: string): string => {
        return value === '1' ? '✓' : '—';
    };

    // --- Table Configuration ---
    const columns: ITableColumn[] = [
        { name: 'Entry No', mapping: 'entryNo' },
        { name: 'Account Number', mapping: 'acctNo' },
        { name: 'Post Date', mapping: 'postDate', format: formatDateForDisplay },
        { name: 'Payee', mapping: 'dscr' },
        { name: 'Description', mapping: 'dscr2' },
        { name: 'Reference', mapping: 'refNo' },
        { name: 'Type', mapping: 'typ' },
        { name: 'Amount', mapping: 'amt', format: formatCurrency },
        { name: 'Cl', mapping: 'clearBank' },
        { name: 'Statement Date', mapping: 'statementDate', format: formatDateForDisplay },
        { name: 'Del', mapping: 'delFlag', format: formatCheckbox },
    ];

    const tableConfig: ITableConfig = {
        rowCount: glData && glData.items ? glData.items.length : 0,
        pageNumber: pageNumber,
        idField: 'id',
        columns: columns,
    };

    if (!isOpen) {
        return null;
    }

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                <style>{`
                    .modal-table-container { display: flex; flex-direction: column; height: 100%; border: none !important; }
                    .modal-table-container table { width: 100%; border-collapse: collapse; text-align: left; margin-bottom: 0 !important; border: none !important; box-shadow: none !important; }
                    .modal-table-container th, .modal-table-container td { border-left: none !important; border-right: none !important; }
                    .modal-table-container th {
                        background-color: #FFFFFF; color: #6B7280; font-weight: 600;
                        font-size: 0.75rem; text-transform: uppercase; padding: 0.75rem 1rem;
                        border-bottom: 2px solid #E5E7EB !important; border-top: none !important;
                        white-space: nowrap; position: sticky; top: 0; z-index: 10;
                    }
                    .modal-table-container td { padding: 0.75rem 1rem; font-size: 0.875rem; color: #111827; border-bottom: 1px solid #F3F4F6 !important; white-space: nowrap; }
                    .modal-table-container tbody tr:last-child td { border-bottom: none !important; }
                    .modal-table-container tbody tr:hover td { background-color: #F9FAFB !important; }
                    .modal-table-container::-webkit-scrollbar { width: 8px; }
                    .modal-table-container::-webkit-scrollbar-track { background: transparent; margin-top: 40px; }
                    .modal-table-container::-webkit-scrollbar-thumb { background-color: #D1D5DB; border-radius: 10px; }
                    .modal-table-container::-webkit-scrollbar-thumb:hover { background-color: #9CA3AF; }
                    .pager-sticky-wrapper {
                        position: sticky !important; bottom: 0 !important; z-index: 20 !important;
                        background-color: #FFFFFF !important; border-top: 1px solid #E5E7EB !important;
                        padding: 1rem 0 0 0 !important; margin: auto 0 0 0 !important; flex-shrink: 0;
                    }
                `}</style>

                <div style={styles.modalHeader}>
                    <h2 style={styles.modalTitle}>Lease Records G/L Inquiry</h2>
                    <button onClick={onClose} style={styles.closeButton}>
                        <X style={{ width: '1.25rem', height: '1.25rem' }} />
                    </button>
                </div>

                <LeaseRecordsErrorAlert error={requestError} onDismiss={() => setRequestError(null)} />

                <div style={styles.modalBody}>
                    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: '1rem', overflow: 'hidden' }}>
                        {/* Filters Grid */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '0.75rem', flexShrink: 0 }}>
                            <LeaseRecordInput
                                style={styles.searchInput}
                                placeholder="Entry No..."
                                value={filterEntryNo}
                                onChange={(e) => setFilterEntryNo(e.target.value)}
                                onKeyPress={handleKeyPress}
                            />
                            <LeaseRecordInput
                                style={styles.searchInput}
                                placeholder="Account Number..."
                                value={filterAcctNo}
                                onChange={(e) => setFilterAcctNo(e.target.value)}
                                onKeyPress={handleKeyPress}
                            />
                            <LeaseRecordInput
                                style={styles.searchInput}
                                placeholder="Post Date (YYYY-MM-DD)..."
                                type="date"
                                value={filterPostDate}
                                onChange={(e) => setFilterPostDate(e.target.value)}
                                onKeyPress={handleKeyPress}
                            />
                            <LeaseRecordInput
                                style={styles.searchInput}
                                placeholder="Payee..."
                                value={filterPayee}
                                onChange={(e) => setFilterPayee(e.target.value)}
                                onKeyPress={handleKeyPress}
                            />
                            <LeaseRecordInput
                                style={styles.searchInput}
                                placeholder="Description..."
                                value={filterDescription}
                                onChange={(e) => setFilterDescription(e.target.value)}
                                onKeyPress={handleKeyPress}
                            />
                            <LeaseRecordInput
                                style={styles.searchInput}
                                placeholder="Reference..."
                                value={filterRefNo}
                                onChange={(e) => setFilterRefNo(e.target.value)}
                                onKeyPress={handleKeyPress}
                            />
                            <LeaseRecordInput
                                style={styles.searchInput}
                                placeholder="Type..."
                                value={filterType}
                                onChange={(e) => setFilterType(e.target.value)}
                                onKeyPress={handleKeyPress}
                            />
                            <LeaseRecordInput
                                style={styles.searchInput}
                                placeholder="Amount..."
                                type="number"
                                value={filterAmount}
                                onChange={(e) => setFilterAmount(e.target.value)}
                                onKeyPress={handleKeyPress}
                            />

                            {/* Cl Filter Dropdown */}
                            <select style={styles.searchInput} value={filterCl} onChange={(e) => setFilterCl(e.target.value)}>
                                <option value="">Cl (All)...</option>
                                <option value="Y">Yes</option>
                                <option value="N">No</option>
                            </select>

                            <LeaseRecordInput
                                style={styles.searchInput}
                                placeholder="Statement Date..."
                                type="date"
                                value={filterStatementDate}
                                onChange={(e) => setFilterStatementDate(e.target.value)}
                                onKeyPress={handleKeyPress}
                            />
                        </div>

                        {/* Toolbar Actions */}
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                flexShrink: 0,
                                paddingBottom: '0.5rem',
                                borderBottom: '1px solid #E5E7EB',
                            }}
                        >
                            <div style={{ display: 'flex', gap: '0.75rem' }}>
                                <button onClick={handleSearch} style={styles.buttonPrimary} disabled={loading}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        {loading && <Loader2 className="animate-spin" style={{ width: '1rem', height: '1rem' }} />}
                                        <span>Search</span>
                                    </div>
                                </button>
                                <button onClick={handleClearFilters} style={styles.buttonSecondary}>
                                    Clear All
                                </button>
                            </div>
                            <button onClick={() => setIsReportModalOpen(true)} style={styles.buttonReport}>
                                Report
                            </button>
                        </div>

                        {/* Table */}
                        <div className="modal-table-container" style={styles.tableWrapper}>
                            <Table isLoading={loading && glData === undefined} tableConfig={tableConfig} data={glData} enablePaging={false} />
                        </div>

                        {/* Footer Pagination */}
                        {glData && glData.totalRecords > 0 && (
                            <div className="pager-sticky-wrapper">
                                <Pager
                                    pageSize={glData.pageSize}
                                    pageNumber={glData.pageNumber}
                                    currentPageCount={glData.items.length}
                                    totalRecords={glData.totalRecords}
                                    onPageChange={handlePageChange}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* --- SUB MODAL: REPORT PARAMETERS --- */}
            {isReportModalOpen && (
                <div style={styles.subModalOverlay} onClick={() => setIsReportModalOpen(false)}>
                    <div style={styles.subModalContent} onClick={(e) => e.stopPropagation()}>
                        <div style={styles.subModalHeader}>
                            <h3 style={styles.subModalTitle}>GL Detail Report Parameters</h3>
                            <button onClick={() => setIsReportModalOpen(false)} style={styles.closeButton}>
                                <X style={{ width: '1rem', height: '1rem' }} />
                            </button>
                        </div>
                        <div style={styles.subModalBody}>
                            <Field
                                label="Account Number (Use % for wildcard)"
                                value={reportAcctNo}
                                editable={true}
                                onChange={(v) => setReportAcctNo(v || '')}
                            />
                            <Field
                                label="Beginning Date"
                                type="date"
                                value={reportBegDate}
                                editable={true}
                                onChange={(v) => setReportBegDate(v || '')}
                            />
                            <Field
                                label="Ending Date"
                                type="date"
                                value={reportEndDate}
                                editable={true}
                                onChange={(v) => setReportEndDate(v || '')}
                            />
                            <SelectField
                                label="Sort Option"
                                value={reportSortType}
                                editable={true}
                                options={[
                                    { value: '1', label: '1 - Reference No' },
                                    { value: '2', label: '2 - Post Date' },
                                ]}
                                onChange={(v) => setReportSortType(v || '1')}
                            />
                        </div>
                        <div style={styles.subModalFooter}>
                            <button onClick={() => setIsReportModalOpen(false)} style={styles.buttonSecondary}>
                                Cancel
                            </button>
                            <button onClick={handleGenerateReport} style={styles.buttonSuccess} disabled={isGeneratingReport}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    {isGeneratingReport && <Loader2 className="animate-spin" style={{ width: '1rem', height: '1rem' }} />}
                                    <span>{isGeneratingReport ? 'Generating...' : 'Download Excel'}</span>
                                </div>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const styles: { [key: string]: React.CSSProperties } = {
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 50,
        padding: '1rem',
        backdropFilter: 'blur(1px)',
    },
    modalContent: {
        backgroundColor: '#FFFFFF',
        borderRadius: '1rem',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        width: '95%',
        maxWidth: '1400px',
        height: '85vh',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '1.5rem 1.5rem 1rem',
        borderBottom: '1px solid #E5E7EB',
    },
    modalTitle: { fontSize: '1.25rem', fontWeight: 600, color: '#111827', margin: 0 },
    closeButton: {
        color: '#9CA3AF',
        padding: '0.25rem',
        borderRadius: '50%',
        backgroundColor: 'transparent',
        border: 'none',
        outline: 'none',
        cursor: 'pointer',
    },
    modalBody: { padding: '1.5rem', display: 'flex', flexDirection: 'column', flexGrow: 1, overflow: 'hidden' },
    searchInput: {
        padding: '0.5rem 0.75rem',
        width: '100%',
        border: '1px solid #D1D5DB',
        borderRadius: '6px',
        fontSize: '0.875rem',
        outline: 'none',
        backgroundColor: '#FFFFFF',
        color: '#111827',
    },
    tableWrapper: { overflowY: 'auto', flexGrow: 1, backgroundColor: 'white', position: 'relative' },

    buttonPrimary: {
        padding: '0.5rem 1.25rem',
        border: 'none',
        outline: 'none',
        borderRadius: '6px',
        backgroundColor: '#4F46E5',
        color: 'white',
        cursor: 'pointer',
        fontWeight: 500,
        fontSize: '0.875rem',
    },
    buttonSecondary: {
        padding: '0.5rem 1.25rem',
        border: '1px solid #D1D5DB',
        outline: 'none',
        borderRadius: '6px',
        backgroundColor: '#FFFFFF',
        color: '#374151',
        cursor: 'pointer',
        fontWeight: 500,
        fontSize: '0.875rem',
    },
    buttonSuccess: {
        padding: '0.5rem 1.25rem',
        border: 'none',
        outline: 'none',
        borderRadius: '6px',
        backgroundColor: '#10B981',
        color: 'white',
        cursor: 'pointer',
        fontWeight: 500,
        fontSize: '0.875rem',
    },
    buttonReport: {
        padding: '0.5rem 1.25rem',
        border: '1px solid #10B981',
        outline: 'none',
        borderRadius: '6px',
        backgroundColor: '#ECFDF5',
        color: '#047857',
        cursor: 'pointer',
        fontWeight: 600,
        fontSize: '0.875rem',
    },

    subModalOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 60,
        backdropFilter: 'blur(2px)',
        borderRadius: '1rem',
    },
    subModalContent: {
        backgroundColor: '#FFFFFF',
        borderRadius: '0.75rem',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2)',
        width: '450px',
        border: '1px solid #E5E7EB',
        display: 'flex',
        flexDirection: 'column',
    },
    subModalHeader: {
        padding: '1rem 1.5rem',
        borderBottom: '1px solid #E5E7EB',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: '#F9FAFB',
        borderTopLeftRadius: '0.75rem',
        borderTopRightRadius: '0.75rem',
    },
    subModalTitle: { margin: 0, fontSize: '1rem', color: '#111827', fontWeight: 600 },
    subModalBody: { padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' },
    subModalFooter: {
        padding: '1rem 1.5rem',
        borderTop: '1px solid #E5E7EB',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '0.75rem',
        backgroundColor: '#F9FAFB',
        borderBottomLeftRadius: '0.75rem',
        borderBottomRightRadius: '0.75rem',
    },
};

export default GLInquiryModal;



// WEBPACK FOOTER //
// ./src/components/leases/sections/GlInquiryModal.tsx